import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Divider, Form, Input, Modal, Select, Tag } from 'antd';
import { interpolateString } from '@utils/string';

import { CANCEL_BUTTON_LABEL } from '@constants/common';
import {
  CONNECT_BUTTON_LABEL,
  CONNECT_MODAL_TITLE,
  CONNECTIONS_CONFIG,
  DISCONNECT_BUTTON_LABEL,
  DISCONNECT_CONFIRMATION_MESSAGE,
  DISCONNECT_CONFIRMATION_TITLE,
  INTEGRATION_TITLE,
  MODEL_PROVIDER_LABEL_CONNECTED,
  MODEL_PROVIDER_LABEL_INVALID,
  MODEL_PROVIDER_LABEL_NO_CONNECTED,
  MODEL_PROVIDERS_NAMES_MAPPED,
} from '@constants/settings-page';
import {
  deleteModelRegistryToken,
  fetchModelRegistryTokens,
  postModelRegistryToken,
  resetMessage,
} from '@redux/actions/organizationActions';
import {
  organizationConnectionSelector,
  organizationMessageSelector,
} from '@redux/selectors/organizationSelectors';
import { IMessage, IModelRegistryToken, ModelProviders } from '@redux/types/types';
import TopNavigation from '@components/settings/TopNavigation';
import styles from './connectionsPage.module.scss';

const { Option } = Select;

const RenderIntegration = ({
  logo,
  provider,
  formItems,
  label,
  labelConnected,
  tokenFormatter,
}: {
  logo: { src: string; description: string };
  provider: ModelProviders;
  formItems: any[];
  label?: string;
  labelConnected?: string;
  tokenFormatter?: (values: any) => string;
}) => {
  const dispatch = useDispatch();
  const { modelRegistryTokens } = useSelector(organizationConnectionSelector);
  const message: IMessage = useSelector(organizationMessageSelector);
  const [confirmationModal, confirmationModalContext] = Modal.useModal();

  const getModelProviderToken = (tokens: IModelRegistryToken[]) => {
    if (!tokens || tokens.length < 1) return null;

    return modelRegistryTokens.find((token: IModelRegistryToken) => token.provider === provider);
  };

  const getTokenDescription = (existingToken: IModelRegistryToken | null) => {
    if (!existingToken) {
      if (label) return label;
      return interpolateString(MODEL_PROVIDER_LABEL_NO_CONNECTED, {
        provider: MODEL_PROVIDERS_NAMES_MAPPED[provider],
        private: provider === ModelProviders.huggingface ? 'private ' : '',
      });
    }

    if (existingToken.invalid) {
      return (
        <Tag icon={<ExclamationCircleOutlined />} color="warning">
          {interpolateString(MODEL_PROVIDER_LABEL_INVALID, {
            provider: MODEL_PROVIDERS_NAMES_MAPPED[provider],
            private: provider === ModelProviders.huggingface ? 'private ' : '',
          })}
        </Tag>
      );
    }

    if (labelConnected) return labelConnected;

    return interpolateString(MODEL_PROVIDER_LABEL_CONNECTED, {
      provider: MODEL_PROVIDERS_NAMES_MAPPED[provider],
      private: provider === ModelProviders.huggingface ? 'private ' : '',
    });
  };

  const existingToken = getModelProviderToken(modelRegistryTokens);
  const [token, setToken] = useState(existingToken);
  const [modalVisible, setModalVisible] = useState(false);

  const onFinish = async (values: any) => {
    let formattedValues = values;
    if (tokenFormatter) {
      formattedValues = tokenFormatter(values);
    } else if (values.token) {
      formattedValues = values.token;
    }

    await dispatch(
      postModelRegistryToken({ modelToken: formattedValues, modelProvider: provider }),
    );
    dispatch(fetchModelRegistryTokens());
  };

  const handleDisconnect = async () => {
    await dispatch(deleteModelRegistryToken(provider));
    setToken(null);
  };

  const handleCancelModal = () => {
    setModalVisible(false);
  };

  useEffect(() => {
    const currentToken = getModelProviderToken(modelRegistryTokens);
    if (currentToken) {
      setModalVisible(false);
      setToken(currentToken);
    } else {
      setToken(null);
    }
  }, [modelRegistryTokens]);

  useEffect(() => {
    if (!modalVisible) dispatch(resetMessage);
  }, [modalVisible]);

  return (
    <>
      <div className={styles.integration}>
        <div className={styles.integration_input}>
          <img src={logo.src} alt={logo.description} className={styles.integration_logo} />
          <div className={styles.integration_input_label}>
            <div>
              <span className={styles.integration_input_label_title}>
                {MODEL_PROVIDERS_NAMES_MAPPED[provider]}
              </span>
            </div>
            <small>{getTokenDescription(existingToken ?? null)}</small>
          </div>
        </div>
        {token ? (
          <Button
            danger
            onClick={async () => {
              const confirmed = await confirmationModal.confirm({
                title: interpolateString(DISCONNECT_CONFIRMATION_TITLE, {
                  provider: MODEL_PROVIDERS_NAMES_MAPPED[provider],
                }),
                content: interpolateString(DISCONNECT_CONFIRMATION_MESSAGE, {
                  provider: MODEL_PROVIDERS_NAMES_MAPPED[provider],
                }),
                okText: DISCONNECT_BUTTON_LABEL,
                okButtonProps: {
                  danger: true,
                },
                centered: true,
              });
              if (confirmed) handleDisconnect();
            }}
          >
            {DISCONNECT_BUTTON_LABEL}
          </Button>
        ) : (
          <Button onClick={() => setModalVisible(true)}>{CONNECT_BUTTON_LABEL}</Button>
        )}
      </div>
      <Modal
        title={interpolateString(CONNECT_MODAL_TITLE, {
          provider: MODEL_PROVIDERS_NAMES_MAPPED[provider],
        })}
        open={modalVisible}
        footer={null}
        classNames={{
          body: styles.integrationModal_body,
        }}
        onCancel={handleCancelModal}
        centered
      >
        {message && message.content && (
          <Alert message={message.content} type={message.type} banner />
        )}
        <Form name={provider} layout="vertical" autoComplete="off" onFinish={onFinish}>
          <div className={styles.integrationModal_form}>
            {formItems.map((item) => (
              <Form.Item
                key={item.name}
                label={item.label}
                name={item.name}
                rules={item.rules}
                initialValue={item.defaultValue}
              >
                {item.options ? (
                  <Select placeholder={item.placeholder}>
                    {item.options.map((option: { value: string; label: string }) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Input placeholder={item.placeholder} />
                )}
              </Form.Item>
            ))}
          </div>
          <Divider />
          <Form.Item className={styles.form_actions}>
            <Button htmlType="reset" onClick={handleCancelModal}>
              {CANCEL_BUTTON_LABEL}
            </Button>
            <Button type="primary" htmlType="submit">
              {CONNECT_BUTTON_LABEL}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      {confirmationModalContext}
    </>
  );
};

const ConnectionsPage: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetMessage);
    dispatch(fetchModelRegistryTokens());
  }, [dispatch]);

  return (
    <div className="content-wrapper">
      <TopNavigation />
      <div className={styles.container}>
        <h5 className={styles.header}>{INTEGRATION_TITLE}</h5>
        <section className={styles.integrations}>
          {CONNECTIONS_CONFIG.map((connection) => (
            <RenderIntegration
              key={connection.provider}
              logo={connection.logo}
              provider={connection.provider}
              formItems={connection.formItems}
              label={connection.label}
              labelConnected={connection.labelConnected}
              tokenFormatter={connection.tokenFormatter}
            />
          ))}
        </section>
      </div>
    </div>
  );
};

export default ConnectionsPage;
